import {
  alertError,
  alertSuccess,
  AsyncButton,
  Button,
  ButtonStyle,
  capitalize,
  Card,
  CardContent,
  CardHeader,
  CardSize,
  colors,
  CurrencyCode,
  Datepicker,
  enumToOptions,
  IGiftItemResponse,
  InputField,
  InputGroup,
  InputType,
  Loading,
  PromoAmountField,
  PromotionMonetaryType,
  PromotionStatus,
  PromotionType,
  SelectField,
  Switch,
  IPromotionResponse,
} from '@one-tree/library'
import moment from 'moment'
import React, { ReactElement, useEffect, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { useOrganisation } from '../../../context/OrganisationProvider'
import {
  createPromotion,
  deletePromotion,
  editPromotion,
  get,
} from '../../../helpers/APIHelper'
import { getGiftItems } from '../../../helpers/APIItemHelper'
import Page from '../../../hoc/Page'
import { useDraft } from '../../../hooks/useDraft'
import { Resource } from '../../../types/API'
import { RoutePath } from '../../../types/Routes'
import GiftItemGrid from '../giftItemGrid/GiftItemGrid'
import {
  convertPromoResponseToPromotion,
  getPromoState,
  isPromoActive,
  nullify,
  PromoState,
  Promotion,
  UpdatePromotion,
} from './PromotionHelper'
import PromotionSwitch from './PromotionSwitch'

const Box = styled.div<{ promoActive: boolean }>`
  background-color: ${({ promoActive }): string => (promoActive ? colors.lightGray : colors.gray)};
  border: 2px solid
    ${({ promoActive }): string => (promoActive ? colors.green : colors.darkGray)};
  transition:
    background-color 400ms,
    border 400ms;
  border-radius: 4px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  row-gap: 20px;
  margin-bottom: 40px;
`
const Settings = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 10px;
`
const Options = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  row-gap: 30px;
`
const OptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  row-gap: 3px;
`
const Label = styled.div`
  font-weight: bold;
  font-size: 0.9rem;
`
const Info = styled.div`
  font-size: 0.9rem;
  max-width: 430px;
  text-wrap: balance;
`

export default function EditPromotion(): ReactElement | null {
  const { id }: { id: string } = useParams()
  const { organisation } = useOrganisation()
  const history = useHistory()

  const location = useLocation()
  const promotions: Promotion[] = location.state?.promotions
  const passedPromo = promotions?.find((promo) => String(promo.id) === id)
  const initLoad = id && !passedPromo // If edit mode, but can't find the promotion

  const [loading, setLoading] = useState(initLoad)
  const [itemsLoading, setItemsLoading] = useState(false)

  const [giftItems, setGiftItems] = useState<IGiftItemResponse[]>()

  const draft = useDraft<Promotion, UpdatePromotion>(passedPromo ?? undefined)
  const { base, setBase, values, changes, handleChange, hasChanges, reset } = draft

  const { Additive, Subtractive } = PromotionMonetaryType
  const [showMonetaryOpts, setShowMonetaryOpts] = useState(false)
  const monetaryOptions = [
    { label: 'Discount (default)', value: Subtractive },
    { label: 'Added Value', value: Additive },
  ]
  const monetaryOption = monetaryOptions.find(
    ({ value }) => value === values.monetaryType,
  )

  const [showScheduleOpts, setShowScheduleOpts] = useState(false)

  const [showCodeMode, setShowCodeMode] = useState(false)

  const fetch = async (promotionId: string): Promise<void> => {
    const res = await get<IPromotionResponse>({
      resource: Resource.Promotions,
      urlPath: [promotionId],
    })
    if (res && res.id) {
      setBase(convertPromoResponseToPromotion(res))
    } else {
      alertError('Promotion not found')
      history.push(RoutePath.ManagePromotions)
    }

    setLoading(false)
  }

  useEffect(() => {
    if (id && !base) fetch(id)
  }, [id])

  useEffect(() => {
    if (base && values.type !== PromotionType.Percentage) {
      if (base.type === PromotionType.Percentage && (values.monetaryType || values.monetaryFloor)) {
        handleChange('monetaryType', undefined)
        handleChange('monetaryFloor', undefined)
        setShowMonetaryOpts(false)
      }
    }
  }, [values.type, base?.type, base])

  useEffect(() => {
    if (base) {
      const isPercentagePromo = base.type === PromotionType.Percentage
      const hasMonetarySettings = base.monetaryType === Additive || base?.monetaryFloor
      if (hasMonetarySettings && isPercentagePromo) setShowMonetaryOpts(true)
      if (
        base.activeFrom
        || base.deactivateOn
        || base.validityStart
        || base.validityEnd
      ) setShowScheduleOpts(true)
      if (base.code) setShowCodeMode(true)
    }
  }, [base])

  const fetchGiftItems = async (): Promise<void> => {
    if (!organisation) return
    setItemsLoading(true)
    const res = await getGiftItems({ format: organisation.format })
    if (res) {
      setGiftItems(
        res.filter((item) => item.id && values.giftItems?.includes(item.id)),
      )
    }
    setItemsLoading(false)
  }

  useEffect(() => {
    if (id && values?.giftItems?.length && !giftItems) fetchGiftItems()
  }, [values?.giftItems])

  // Pass the cached promotions list back if no data changed
  const redirect = (): void => {
    history.goBack()
  }

  const handleDelete = async (): Promise<void> => {
    const res = await deletePromotion({ id })
    if (res) {
      alertSuccess('Promotion deleted')
      reset()
      redirect()
    }
  }

  const handleSave = async (): Promise<void> => {
    if (
      values.activeFrom
      && values.deactivateOn
      && values.deactivateOn <= values.activeFrom
    ) {
      alertError('Deactivation date must be after the activation date')
      return
    }
    if (
      values.validityStart
      && values.validityEnd
      && values.validityEnd <= values.validityStart
    ) {
      alertError('Voucher valid until date must be after the valid from date')
      return
    }

    // Change 'undefined' to 'null' to clear fields when sending to API
    const changesWithNull = nullify(changes)

    if (!id) {
      const res = await createPromotion({ promotion: changesWithNull })
      if (res) {
        reset()
        alertSuccess('Promotion created')
        redirect()
      }
    } else {
      // Back-end limitation: have to always send the giftItems
      const changesWithItems = {
        giftItems: values.giftItems,
        ...changesWithNull,
      }
      const res = await editPromotion({
        id: Number(id),
        promotion: changesWithItems,
      })

      if (res) {
        reset()
        setBase(convertPromoResponseToPromotion(res))
        alertSuccess('Promotion saved')
        redirect()
      }
    }
  }

  const handleClone = async (): Promise<void> => {
    const clone: Partial<Promotion> = { ...values }
    delete clone.id
    delete clone.sales

    const res = await createPromotion({ promotion: clone })
    if (res) {
      alertSuccess('Promotion cloned')
      history.push(`${RoutePath.ManagePromotions}/${res.id}`)
    } else {
      alertError('There was an error cloning the promotion')
    }
  }

  const handleSwitch = (value: boolean): void => {
    handleChange(
      'status',
      value ? PromotionStatus.Active : PromotionStatus.Inactive,
    )
  }

  const promoStatus = getPromoState(values)
  const promoWithinSchedule = promoStatus === PromoState.WithinSchedule
  const promoScheduled = promoStatus === PromoState.Scheduled
  const pastSchedule = promoStatus === PromoState.PastSchedule
  const missingRequired = !values?.name || !values?.type || !values?.amount
  const promoActive = isPromoActive(values)

  const disableSave = !hasChanges || missingRequired
  const disableSwitch = promoScheduled || promoWithinSchedule || missingRequired || pastSchedule

  // Add our values to the items so we get a live updating preview
  const itemPromotion: IPromotionResponse | undefined = !values.code
    ? {
        id: Number(id),
        name: values.name || '',
        type: values.type || PromotionType.Percentage,
        amount: values.amount || 0,
        wording: values.wording || '',
        validityStart: values.validityStart,
        validityEnd: values.validityEnd,
        monetaryType: values.monetaryType,
        monetaryFloor: values.monetaryFloor,
        promotionTerms: values.promotionTerms,
        addOn: values.addOn,
        giftItems: [],
        status: PromotionStatus.Active,
        sales: 0,
      }
    : undefined // Code mode items should not visually show a discount

  const giftItemsWithPromo = giftItems?.map((item) => ({
    ...item,
    promotion: itemPromotion,
  }))

  return (
    <Page warnBeforeExit={hasChanges}>
      <Card>
        <CardHeader title={id ? 'Edit promotion' : 'Create new promotion'}>
          <Button buttonStyle={ButtonStyle.Secondary} onClick={redirect}>
            Cancel
          </Button>
          {id && (
            <>
              <Button onClick={handleClone}>Clone</Button>
              <AsyncButton
                buttonStyle={ButtonStyle.Danger}
                onClick={handleDelete}
                showConfirmation={true}
              >
                Delete
              </AsyncButton>
            </>
          )}
          <AsyncButton
            buttonStyle={ButtonStyle.Action}
            onClick={handleSave}
            disabled={disableSave}
          >
            Save
          </AsyncButton>
        </CardHeader>
        {!loading ? (
          <CardContent>
            <Box promoActive={promoActive}>
              <InputField
                label="Name"
                value={values?.name}
                onChange={(value): void => handleChange('name', value)}
                maxChars={30}
              />
              <InputGroup>
                <SelectField
                  label="Type of discount"
                  value={{
                    label: capitalize(values.type),
                    value: values.type,
                  }}
                  onChange={(option): void => {
                    if (option?.value) {
                      handleChange('amount', undefined) // Clear amount when type changes
                      handleChange('type', option.value)
                    }
                  }}
                  options={enumToOptions(PromotionType)}
                  isLong={true}
                />
                <PromoAmountField
                  key={values?.type} // Re-render to clear field when type changes
                  style={{ width: '100px' }}
                  label="Discount amount"
                  value={values?.amount}
                  onChange={(value): void => handleChange('amount', value)}
                  promotionType={values?.type || PromotionType.Percentage}
                  currency={organisation?.currencyCode || CurrencyCode.GBP}
                />
              </InputGroup>
              <InputGroup style={{ justifyContent: 'space-between' }}>
                <Switch
                  label={capitalize(promoStatus)}
                  value={promoActive}
                  switchAction={handleSwitch}
                  flipLabel={true}
                  disabled={disableSwitch}
                />
                {base?.sales ? <div>Sales: {base.sales}</div> : null}
              </InputGroup>
            </Box>
            <Settings>
              <h3>Settings</h3>
              <Options>
                {!showCodeMode && (
                  <InputField
                    label="Callout wording"
                    onChange={(value): void => handleChange('wording', value)}
                    value={values.wording}
                    maxChars={50}
                    isLong={true}
                  />
                )}
                <PromotionSwitch
                  show={showScheduleOpts}
                  switchLabel="Schedule promotion dates"
                  switchAction={(how): void => {
                    setShowScheduleOpts(how)
                    if (!how) {
                      handleChange('activeFrom', undefined)
                      handleChange('deactivateOn', undefined)
                      handleChange('validityStart', undefined)
                      handleChange('validityEnd', undefined)
                    }
                  }}
                >
                  <OptionContainer>
                    <Label>Promotion active from date/time</Label>
                    <Info>
                      This is when gifts with this promotion applied become
                      discounted. Leave blank to start immediately.
                    </Info>
                    <Datepicker
                      time={true}
                      value={
                        values.activeFrom
                          ? moment(values.activeFrom, 'YYYY-MM-DD HH:mm:ss')
                          : null
                      }
                      onChange={(date): void => handleChange('activeFrom', date?.format('YYYY-MM-DD HH:mm:ss'))}
                      isClearable={true}
                      futureOnly={true}
                    />
                  </OptionContainer>
                  <OptionContainer>
                    <Label>Promotion deactivates on date/time</Label>
                    <Info>
                      This is when gifts with this promotion applied return to
                      their normal price. Leave blank to run this promotion
                      continuously.
                    </Info>
                    <Datepicker
                      time={true}
                      value={
                        values.deactivateOn
                          ? moment(values.deactivateOn, 'YYYY-MM-DD HH:mm:ss')
                          : null
                      }
                      onChange={(date): void => {
                        if (date) {
                          const hadPreviousValue = values.deactivateOn !== undefined
                          if (hadPreviousValue) {
                            handleChange('deactivateOn', date.format('YYYY-MM-DD HH:mm:ss'))
                          } else {
                            handleChange('deactivateOn', date.hours(23).minutes(59).seconds(0).format('YYYY-MM-DD HH:mm:ss'))
                          }
                        } else {
                          handleChange('deactivateOn', undefined)
                        }
                      }}
                      isClearable={true}
                      futureOnly={true}
                    />
                  </OptionContainer>
                  <OptionContainer>
                    <Label>Vouchers valid from date</Label>
                    <Info>
                      This is when vouchers purchased under this promotion
                      become redeemable. Leave blank for vouchers to be valid
                      immediately.
                    </Info>
                    <Datepicker
                      value={
                        values.validityStart
                          ? moment(values.validityStart, 'YYYY-MM-DD')
                          : null
                      }
                      onChange={(date): void => handleChange('validityStart', date?.format('YYYY-MM-DD'))}
                      isClearable={true}
                      futureOnly={true}
                    />
                  </OptionContainer>
                  <OptionContainer>
                    <Label>Vouchers valid until date</Label>
                    <Info>
                      This is when vouchers purchased under this promotion
                      expire. Leave blank to use each voucher&apos;s existing
                      validity period.
                    </Info>
                    <Datepicker
                      value={
                        values.validityEnd
                          ? moment(values.validityEnd, 'YYYY-MM-DD')
                          : null
                      }
                      onChange={(date): void => handleChange('validityEnd', date?.format('YYYY-MM-DD'))}
                      isClearable={true}
                      futureOnly={true}
                    />
                  </OptionContainer>
                </PromotionSwitch>
                {values.type === PromotionType.Percentage && (
                <PromotionSwitch
                  show={showMonetaryOpts}
                  switchLabel="Custom monetary voucher settings"
                  switchAction={(how): void => {
                    setShowMonetaryOpts(how)
                    if (!how) {
                      handleChange('monetaryType', undefined)
                      handleChange('monetaryFloor', undefined)
                    }
                  }}
                >
                  <OptionContainer>
                    <Label>Monetary voucher handling</Label>
                    <Info>For a £10 monetary voucher with a 20% discount:</Info>
                    <Info>
                      Discount = customer gets a £10 voucher for only £8.
                    </Info>
                    <Info>
                      Added Value = customer spends £10 and gets a £12 voucher.
                    </Info>
                    <SelectField
                      options={monetaryOptions}
                      value={monetaryOption}
                      onChange={(option): void => {
                        if (option) handleChange('monetaryType', option.value)
                      }}
                    />
                  </OptionContainer>
                  <OptionContainer>
                    <Label>Monetary voucher floor</Label>
                    <Info>
                      Only apply the discount to amounts equal to or above this
                      value (monetary vouchers only)
                    </Info>
                    <InputField
                      type={InputType.Number}
                      value={values.monetaryFloor?.toString() || '0'}
                      onChange={(value): void => handleChange('monetaryFloor', Number(value))}
                    />
                  </OptionContainer>
                </PromotionSwitch>
                )}

                <PromotionSwitch
                  show={showCodeMode}
                  switchLabel="Code mode"
                  switchAction={(how): void => {
                    setShowCodeMode(how)
                    if (!how) handleChange('code', undefined)
                  }}
                >
                  <OptionContainer>
                    <Label>Voucher checkout code</Label>
                    <Info>
                      &quot;Code mode&quot; promotions are not shown on the
                      vouchers in the Web Shop. Customers instead apply this
                      code at checkout.
                    </Info>
                    <InputField
                      onChange={(value): void => handleChange('code', value)}
                      value={values.code}
                    />
                  </OptionContainer>
                </PromotionSwitch>
              </Options>
            </Settings>
          </CardContent>
        ) : (
          <Loading fullPage={true} />
        )}
      </Card>
      {itemsLoading || giftItemsWithPromo ? (
        <Card cardSize={CardSize.Big}>
          <CardHeader title="Gifts with this promotion" />
          <CardContent>
            {itemsLoading ? <Loading fullPage={true} /> : null}
            {giftItemsWithPromo ? (
              <GiftItemGrid
                key={JSON.stringify(itemPromotion)} // Force re-render when promo changes
                giftItemData={giftItemsWithPromo}
              />
            ) : null}
          </CardContent>
        </Card>
      ) : null}
    </Page>
  )
}
